import {ESRNotificationGroup, ESRNotificationT, ESRState, SysNotificationT, Thread} from "../types/data";
import {
	EXTERNAL_FETCH_COMPLETE,
	EXTERNAL_FETCH_IN_PROGRESS,
	IS_USER_PERMITTED_TO_VIEW_ESR,
	RESET_ESR_DETAILS,
	RESET_ESR_DETAILS_COMPLETE,
	RESET_ESR_DETAILS_IN_PROGRESS,
	RESET_ESR_QUERY,
	RESET_IS_USER_PERMITTED_TO_VIEW_ESR,
	RESET_NOTIFICATIONS_QUERY,
	UPDATE_ADDENDUM_RESPONSES,
	UPDATE_DISPLAYED_RESPONSES,
	UPDATE_ESR_LIST,
	UPDATE_ERROR_LIST,
	UPDATE_TICKET_COMMENTS,
	UPDATE_ESR_PERMISSIONS_LIST,
	UPDATE_ESR_QUERY,
	UPDATE_ESR_SPONSOR_TYPE,
	UPDATE_ESR_SUMMARY,
	UPDATE_NOTIFICATION_STATUS,
	UPDATE_NOTIFICATIONS,
	UPDATE_NOTIFICATIONS_QUERY,
	UPDATE_BULK_RESPONSE_IN_PROGRESS,
	UPDATE_BULK_RESPONSE_COMPLETE,
	UPDATE_QUESTION_RESPONSES,
	UPDATE_REVIEW_SUMMARY,
	UPDATE_REQUEST_FOR_INFO_REVIEW_SUMMARY,
	UPDATE_SYSTEM_NOTIFICATIONS,
	UPDATE_USER_ADMIN,
	UPDATE_USER_APPROVAL,
	UPDATE_USER_QUERY,
	SEND_MESSAGE_IN_PROGRESS,
	SEND_MESSAGE_PROGRESS_COMPLETE,
	SEND_MESSAGE_COMPLETE,
    UPDATE_ESR_LAST_QUESTION_ID,
} from "../actions/form";
import {
	CLOSE_NOTIFICATION,
	LIST_FILTERED_MESSAGES,
	UNREAD_MESSAGES_COUNT,
	LIST_MESSAGES,
	LIST_ALL_MESSAGES,
	LIST_SENT_NOTIFICATIONS,
	SET_NEW_NOTIFICATIONS,
	FAB_LOADING_THREADS_START,
	FAB_LOADING_THREADS_END,
} from "../actions/util";
import { DataAction } from "../types/action";
import { getCurrentUser } from "../auth";

// or initialState
const defaultState: ESRState = {
    last_updated_question_id: "",
	current_user: getCurrentUser(),
	questions: {
		changed: false,
		groups: [],
		complete: false,
	},
	loading: {},
	responses: {},
	committed_responses: {},
	concurrent_edits: {},
	all_responses: {},
	esr_facets: [],
	error_tickets: [],
	ticket_comments: [],
	esr_query: {
		page: 0,
		page_size: 10,
	},
	esr_total_count: 0,
	esr_list_current_page: [],
	identifiers: {
		description: "",
		contract_no: "",
		section_no: "",
		project_no: "",
		route: "",
		from_to: "",
		submission_status: "",
	},
	esr_submitted: false,
	esr_timestamp: "",
	esr_sponsor_type: "",
	natural_review: false,
	cultural_review: false,
	regulated_review: false,
	review_summary: {},
	request_for_more_info: {},
	filenames: null,
	addendums: null,
	description: "",
	dataset_id: "",
	shapeVersion: 0,
	notifications: [],
	system_notifications: {} as Record<string, SysNotificationT[]>,
	notifications_facets: [],
	notificationlist_query: {},
	sent_notifications: [],
	threads: {},
	allThreads: {},
	allmessageCount: 0,
	allmessagePage: 1,
	allmessagePageSize: 25,
	allmessageTotalPages: 0,
	drawer: {
		threadsOpen: false,
		threadsLoading: false,
		attachmentsOpen: false,
		sentNotificationsOpen: false,
		selectedThreadId: -1,
		filters: {},
	},
	fab: {
		fabLoadingThreads: false,
	},
	clipboard: [],
	user_sort_query: {
		page: 0,
		page_size: 10,
	},
	user_list: {},
	user_facets: [],
	permissions: {},
	is_user_permitted: true,
	error: null,
	responder_ids: [],
	current_folder: {
		loading: false,
		folder_id: "",
		path: [],
		read_only: true,
		content: [],
	},
	email_notification_daily_report: false,
	email_notification_weekly_report: false,
	open_notifications: [],
	unread_message_count: 0,
	filtered_threads: {},
	fetch_external: {
		inProgress: false,
		complete: true,
	},
	esr_reset: {
		inProgress: false,
		complete: true,
	},
	review_data: {
		review_esrId: "",
		review_esr_responses: {},
	},
	review_esr_id: "",
	update_bulk_responses_progress: {
		inProgress: false,
		complete: true,
	},
	sending_message_progress: {
		inProgress: false,
		complete: true,
		exit: true,
	},
	request_info_from_sponsor: false,
	is_user_sponsor_in_esr: false,
	is_organization_viewer_only: false,
};

function getLatestVisibleTimestamp(
	notifications: ESRNotificationT[],
	statusFilter?: string[],
): number {
	const visible = statusFilter?.length
		? notifications.filter((notif) => statusFilter.includes(String(notif.status)))
		: notifications;
	if (visible.length === 0) return 0;
	return Math.max(...visible.map((notif) => new Date(notif.timestamp).getTime()));
}

function sortNotificationGroups(
	groups: ESRNotificationGroup[],
	statusFilter?: string[],
): ESRNotificationGroup[] {
	return [...groups].sort(
		(groupA, groupB) =>
			getLatestVisibleTimestamp(groupB.notifications, statusFilter) -
			getLatestVisibleTimestamp(groupA.notifications, statusFilter),
	);
}

function reducer(state = defaultState, action: DataAction): ESRState {
	switch (action.type) {
		case "SET_USER":
			return {
				...state,
				current_user: action.user,
			};
		case UPDATE_USER_APPROVAL:
			return Object.assign({}, state, {
				user_list: {
					...state.user_list,
					[action.email]: {
						...state.user_list[action.email],
						enabled: action.enabled,
					},
				},
			});
		case UPDATE_USER_ADMIN:
			return Object.assign({}, state, {
				user_list: {
					...state.user_list,
					[action.email]: {
						...state.user_list[action.email],
						admin: action.admin,
					},
				},
			});
		case UPDATE_ESR_QUERY:
			return {
				...state,
				esr_query: {
					...state.esr_query,
					...action.updates,
				},
			};
		case UPDATE_NOTIFICATIONS_QUERY:
			return {
				...state,
				notificationlist_query: {
					...state.notificationlist_query,
					...action.updates,
				},
			};
		case SEND_MESSAGE_IN_PROGRESS:
			return {
				...state,
				sending_message_progress: {
					complete: false,
					inProgress: true,
					exit: false,
				},
			};
		case SEND_MESSAGE_PROGRESS_COMPLETE:
			return {
				...state,
				sending_message_progress: {
					complete: true,
					inProgress: false,
					exit: false,
				},
			};
		case SEND_MESSAGE_COMPLETE:
			return {
				...state,
				sending_message_progress: {
					complete: true,
					inProgress: false,
					exit: true,
				},
			};
		case UPDATE_BULK_RESPONSE_COMPLETE:
			return {
				...state,
				update_bulk_responses_progress: {
					complete: true,
					inProgress: false,
				},
			};
		case UPDATE_BULK_RESPONSE_IN_PROGRESS:
			return {
				...state,
				update_bulk_responses_progress: {
					complete: false,
					inProgress: true,
				},
			};
		case UPDATE_ESR_LIST:
			return {
				...state,
				esr_list_current_page: action.current_page,
				esr_total_count: action.total,
				esr_facets: action.facets,
			};
		case UPDATE_ERROR_LIST:
			return {
				...state,
				error_tickets: action.data,
			};
		case UPDATE_TICKET_COMMENTS:
			return {
				...state,
				ticket_comments: {
					...state.ticket_comments,
					[action.ticketId]: action.comments,
				},
			};
		case UPDATE_NOTIFICATIONS: {
			const statusFilter = state.notificationlist_query.status as
				| string[]
				| undefined;
			return {
				...state,
				notifications: sortNotificationGroups(
					action.notifications,
					statusFilter,
				),
				notifications_facets: action.facets,
			};
		}
		case UPDATE_NOTIFICATION_STATUS:
			return {
				...state,
				notifications: state.notifications.map((group) => ({
					...group,
					notifications: group.notifications.map((notif) =>
						notif.id === action.notif_id
							? { ...notif, status: action.status }
							: notif,
					),
				})),
			};
		case UPDATE_SYSTEM_NOTIFICATIONS:
			return {
				...state,
				system_notifications: action.sys_notifications,
			};
		case UPDATE_ESR_SUMMARY:
			return {
				...state,
				esr_submitted: action.esr_submitted,
				esr_timestamp: action.esr_timestamp,
				esr_sponsor_type: action.esr_sponsor_type,
				cultural_review: action.cultural_review,
				natural_review: action.natural_review,
				regulated_review: action.regulated_review,
				addendums: action.addendums,
				description: action.description,
			};
		case RESET_ESR_DETAILS_IN_PROGRESS:
			return {
				...state,
				esr_reset: {
					inProgress: true,
					complete: false,
				},
			};
		case RESET_ESR_DETAILS_COMPLETE:
			return {
				...state,
				esr_reset: {
					inProgress: false,
					complete: true,
				},
			};
		case UPDATE_ESR_PERMISSIONS_LIST:
			return Object.assign({}, state, {
				permissions: action.permissions,
			});
		case IS_USER_PERMITTED_TO_VIEW_ESR:
			return {
				...state,
				is_user_permitted: action.is_user_permitted,
			};
		case UPDATE_REVIEW_SUMMARY:
			return Object.assign({}, state, {
				review_summary: action.summary,
			});
		case UPDATE_REQUEST_FOR_INFO_REVIEW_SUMMARY:
			return Object.assign({}, state, {
				request_for_more_info: action.summary,
			});
		case UPDATE_ESR_SPONSOR_TYPE:
			return Object.assign({}, state, {
				esr_sponsor_type: action.esr_sponsor_type,
			});
		case UPDATE_QUESTION_RESPONSES:
			return {
				...state,
				questions: action.questions,
				responses: action.responses,
				committed_responses: action.committed_responses,
				concurrent_edits: action.concurrent_edits,
				responder_ids: action.responder_ids,
				all_responses: action.all_responses,
				esr_submitted: action.esr_submitted,
				is_user_sponsor_in_esr: action.is_user_sponsor_in_esr,
				is_organization_viewer_only: action.is_organization_viewer_only,
			};
		case "SET_CONCURRENT_EDITS":
			return {
				...state,
				concurrent_edits: action.concurrent_edits,
			};
		case "UPDATE_FILENAME":
			return {
				...state,
				filenames: {
					...state.filenames,
					[action.file_id]: action.filename,
				},
			};
		case UPDATE_ADDENDUM_RESPONSES:
			return {
				...state,
				esr_submitted: action.esr_submitted,
				questions: action.questions,
				responses: action.responses,
				responder_ids: action.responder_ids,
				all_responses: {
					...state.all_responses,
					[action.addendum_label]: {
						esr_id: action.esr_id,
						questions: action.questions,
						responses: action.responses,
						responder_ids: action.responder_ids,
					},
				},
			};
		case UPDATE_DISPLAYED_RESPONSES:
			return {
				...state,
				questions: action.questions,
				responses: action.responses,
				responder_ids: action.responder_ids,
			};
		case "BUMP_SHAPE_VERSION":
			return {
				...state,
				shapeVersion: state.shapeVersion + 1,
			};
		case LIST_MESSAGES:
			const sortedMessages: Record<number, Thread> = Object.values(
				action.messages,
			).sort(
				(a, b) =>
					new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
			);
			return {
				...state,
				threads: sortedMessages,
			};
		case LIST_ALL_MESSAGES:
			const sMessages: Thread[] = Object.values(action.messages).sort(
				(a, b) =>
					new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
			);
			return {
				...state,
				allThreads: sMessages,
				allmessageCount: action.count,
				allmessagePage: action.page,
				allmessagePageSize: action.pageSize,
				allmessageTotalPages: action.totalPages,
			};
		case UNREAD_MESSAGES_COUNT:
			return {
				...state,
				unread_message_count: action.unread_message_count,
			};
		case FAB_LOADING_THREADS_START:
			return {
				...state,
				fab: {
					...state.fab,
					fabLoadingThreads: true,
				},
			};
		case FAB_LOADING_THREADS_END:
			return {
				...state,
				fab: {
					...state.fab,
					fabLoadingThreads: false,
				},
			};
		case LIST_FILTERED_MESSAGES:
			let message_count = 0;
			const sortedThreads: Record<number, Thread> = Object.values(
				action.filtered_threads,
			)
				.map((thread) => {
					if (thread?.unread_messages > 0) {
						message_count += thread.unread_messages;
					}
					return thread;
				})
				.sort(
					(a, b) =>
						new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
				);
			return {
				...state,
				filtered_threads: sortedThreads,
				unread_message_count: message_count,
			};
		case LIST_SENT_NOTIFICATIONS:
			return {
				...state,
				sent_notifications: action.sent_notifications,
			};
		case SET_NEW_NOTIFICATIONS:
			return {
				...state,
				open_notifications: action.open_notifications,
			};
		case CLOSE_NOTIFICATION:
			return {
				...state,
				open_notifications: state.open_notifications.filter(
					(_, index) => index !== action.index,
				),
			};
		case EXTERNAL_FETCH_COMPLETE:
			return {
				...state,
				fetch_external: {
					complete: true,
					inProgress: false,
				},
			};
		case EXTERNAL_FETCH_IN_PROGRESS:
			return {
				...state,
				fetch_external: {
					complete: false,
					inProgress: true,
				},
			};
		case "UPDATE_CURRENT_FOLDER":
			return {
				...state,
				current_folder: {
					...state.current_folder,
					...action.update,
				},
			};
		case "UPDATE_CLIPBOARD":
			return {
				...state,
				clipboard: action.update,
			};
		case "UPDATE_DRAWER_STATE":
			return {
				...state,
				drawer: {
					...state.drawer,
					...action.update,
				},
			};

		case "REQUEST_INFO_FROM_SPONSOR":
			return {
				...state,
				request_info_from_sponsor: true,
			};

		case "REQUEST_INFO_FROM_SPONSOR_COMPLETE":
			return {
				...state,
				request_info_from_sponsor: false,
			};

		case UPDATE_USER_QUERY:
			return {
				...state,
				user_sort_query: {
					...state.user_sort_query,
					...action.updates,
				},
			};
		case "LIST_ALL_USERS":
			const user_lookup: Record<string, any> = {};
			action.users.forEach((u) => {
				const email: string = u["email"];
				user_lookup[email] = u;
			});
			return {
				...state,
				user_list: user_lookup,
				user_facets: action.facets ?? [],
			};
		case "RESET_USER_QUERY":
			return {
				...state,
				user_sort_query: {
					enabled: ["true"],
					page: 0,
					page_size: 10,
				},
			};
		case "ADD_USER_TO_GROUPS":
			const new_groups = JSON.parse(action.groups).filter(
				(v: string) => !state.user_list[action.email]?.groups.includes(v),
			);
			return Object.assign({}, state, {
				user_list: {
					...state.user_list,
					[action.email]: {
						...state.user_list[action.email],
						groups: [
							...(state.user_list[action.email]?.groups ?? []),
							...new_groups,
						],
					},
				},
			});
		case "REMOVE_USER_FROM_GROUP":
			let trimmed_groups: string[] = [...state.user_list[action.email].groups];
			trimmed_groups.splice(trimmed_groups.indexOf(action.group), 1);
			return Object.assign({}, state, {
				user_list: {
					...state.user_list,
					[action.email]: {
						...state.user_list[action.email],
						groups: trimmed_groups,
					},
				},
			});
		case "SET_QUESTION_LOADING_STATE":
			return {
				...state,
				loading: {
					...state.loading,
					[action.question_id]: action.loading,
				},
			};
		case "SET_ERROR":
			return {
				...state,
				error: action.error,
			};
		case "UPDATE_EMAIL_NOTIFICATION_FREQUENCY":
			return {
				...state,
				email_notification_daily_report: action.daily_report,
				email_notification_weekly_report: action.weekly_report,
			};
		case RESET_ESR_QUERY:
			return {
				...state,
				esr_query: {
					page: 0,
					page_size: 10,
					search: "",
				},
			};
		case RESET_NOTIFICATIONS_QUERY:
			return {
				...state,
				notificationlist_query: {},
			};
		case RESET_IS_USER_PERMITTED_TO_VIEW_ESR:
			return {
				...state,
				is_user_permitted: true,
			};
		case RESET_ESR_DETAILS:
			return {
				...state,
				identifiers: defaultState.identifiers,
				esr_submitted: defaultState.esr_submitted,
				esr_timestamp: defaultState.esr_timestamp,
				esr_sponsor_type: defaultState.esr_sponsor_type,
				cultural_review: defaultState.cultural_review,
				natural_review: defaultState.natural_review,
				regulated_review: defaultState.regulated_review,
				filenames: defaultState.filenames,
				dataset_id: defaultState.dataset_id,
				current_folder: defaultState.current_folder,
				addendums: defaultState.addendums,
				review_summary: defaultState.review_summary,
				request_for_more_info: defaultState.request_for_more_info,
				questions: defaultState.questions,
				responses: defaultState.responses,
				all_responses: defaultState.all_responses,
				responder_ids: defaultState.responder_ids,
				threads: defaultState.threads,
				drawer: defaultState.drawer,
			};
		case "UPDATE_ESR_LAST_QUESTION_ID":
			return {
				...state,
				last_updated_question_id: action.last_updated_question_id,
			};
		default:
			return state;
	}
}

export default reducer;
